import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Mail, Calendar, CheckCircle, Copy } from "lucide-react";
import { api } from "../../lib/api";
import { formatCurrency, formatDateTime } from "../../lib/utils-format";
import { SearchableSelect } from "../../components/SearchableSelect";

export function AdminBookingsPage() {
    const { t, i18n } = useTranslation();
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const locale = i18n.language === "fr" ? "fr-FR" : "en-US";

    const load = async () => {
        const { data } = await api.get("/api/admin/bookings");
        setBookings(data || []);
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const send = async (bid) => {
        try { await api.post(`/api/bookings/${bid}/send-tickets`); toast.success(t("bookings.tickets_sent")); } catch { toast.error(t("common.error")); }
    };

    return (
        <div data-testid="admin-bookings-page" className="space-y-4">
            <h1 className="font-heading font-black text-3xl">{t("admin.bookings")}</h1>
            {loading ? (
                <div className="text-muted-foreground">{t("common.loading")}</div>
            ) : bookings.length === 0 ? (
                <div className="rounded-2xl border-2 border-dashed border-border p-12 text-center text-muted-foreground">No bookings.</div>
            ) : (
                <div className="space-y-3">
                    {bookings.map((b) => (
                        <div key={b.id} data-testid={`admin-booking-${b.id}`} className="bento-card p-5 flex flex-col md:flex-row gap-4 items-start">
                            <div className="flex-1 space-y-2">
                                <div className="flex items-center gap-2">
                                    <h3 className="font-bold">{b.event?.title}</h3>
                                    <span className={`badge-pill ${b.status === "confirmed" ? "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]" : "bg-muted"}`}>{b.status}</span>
                                </div>
                                <div className="text-xs text-muted-foreground flex items-center gap-2">
                                    <Calendar className="h-3 w-3" />
                                    {formatDateTime(b.event?.start_datetime, locale)}
                                </div>
                                <div className="text-sm">
                                    <strong>{b.billing_first_name} {b.billing_last_name}</strong> - {b.billing_email}
                                </div>
                                <div className="text-xs text-muted-foreground">{b.billing_phone_code} {b.billing_phone}</div>
                                <div className="text-xs text-muted-foreground">{b.billing_address}, {b.billing_city}, {b.billing_state}, {b.billing_country}</div>
                                <div className="flex flex-wrap gap-1 pt-1">
                                    {(b.tickets || []).map((tk) => (
                                        <span key={tk.ticket_id} className="badge-pill bg-muted text-xs">{tk.ticket_type_name} ({tk.status})</span>
                                    ))}
                                </div>
                            </div>
                            <div className="text-right space-y-2 w-full md:w-auto">
                                <div className="text-xs text-muted-foreground uppercase">{t("booking.total")}</div>
                                <div className="font-heading font-black text-xl">{formatCurrency(b.total_paid, b.currency_code)}</div>
                                <div className="text-xs">{b.payment_method}</div>
                                <button data-testid={`admin-send-${b.id}`} onClick={() => send(b.id)} className="btn-outline !py-2 !px-4 text-sm inline-flex items-center gap-2 w-full"><Mail className="h-4 w-4" /> {t("bookings.send_tickets_email")}</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export function AdminEventsPage() {
    const { t, i18n } = useTranslation();
    const [events, setEvents] = useState([]);
    const locale = i18n.language === "fr" ? "fr-FR" : "en-US";

    const load = async () => {
        const { data } = await api.get("/api/admin/events");
        setEvents(data || []);
    };
    useEffect(() => { load(); }, []);

    const toggle = async (id, cur) => {
        const status = cur === "live" ? "pending" : "live";
        await api.put(`/api/admin/events/${id}/status`, { status });
        load();
    };

    return (
        <div data-testid="admin-events-page" className="space-y-4">
            <h1 className="font-heading font-black text-3xl">{t("admin.events")}</h1>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {events.map((e) => (
                    <div key={e.id} data-testid={`admin-event-${e.id}`} className="bento-card overflow-hidden">
                        <div className="aspect-square bg-muted relative">
                            {e.featured_image_url && <img src={e.featured_image_url} alt="" className="w-full h-full object-cover" />}
                            <span className={`absolute top-2 right-2 badge-pill text-xs ${e.status === "live" ? "bg-[hsl(var(--success))] text-white" : "bg-[hsl(var(--warning))] text-white"}`}>{e.status}</span>
                        </div>
                        <div className="p-3 space-y-1.5">
                            <h3 className="font-bold text-sm line-clamp-2 leading-snug">{e.title}</h3>
                            <div className="text-[10px] text-muted-foreground">{formatDateTime(e.start_datetime, locale)}</div>
                            <div className="text-[10px] text-muted-foreground truncate">by {e.organizer?.organizer_name || e.organizer?.email}</div>
                            <div className="flex gap-1 pt-1">
                                <button onClick={() => toggle(e.id, e.status)} data-testid={`toggle-${e.id}`} className="btn-outline !py-1 !px-2 text-[10px] flex-1">{e.status === "live" ? t("admin.toggle_pending") : t("admin.toggle_live")}</button>
                                <Link to={`/organizer/events/${e.id}/manage`} data-testid={`admin-manage-${e.id}`} className="btn-primary !py-1 !px-2 text-[10px] flex-1 text-center">{t("admin.manage")}</Link>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function AdminPayoutsPage() {
    const { t } = useTranslation();
    const [list, setList] = useState([]);
    const load = async () => { const { data } = await api.get("/api/admin/payouts"); setList(data || []); };
    useEffect(() => { load(); }, []);

    const process = async (id) => {
        await api.put(`/api/admin/payouts/${id}/process`);
        toast.success(t("common.success"));
        load();
    };

    const copyToClipboard = async (value) => {
        if (!value) return;
        try {
            await navigator.clipboard.writeText(String(value));
            toast.success(t("admin.copied"));
        } catch {
            // Fallback for older browsers / insecure contexts
            const ta = document.createElement("textarea");
            ta.value = String(value);
            document.body.appendChild(ta);
            ta.select();
            try { document.execCommand("copy"); toast.success(t("admin.copied")); }
            catch { toast.error(t("common.error")); }
            document.body.removeChild(ta);
        }
    };

    const buildPayoutFields = (d) => {
        if (!d) return [];
        if (d.type === "bank_transfer") {
            return [
                { label: t("wallet.payee_name"), value: d.payee_name },
                { label: t("wallet.account_number"), value: d.account_number },
                { label: t("wallet.routing_number"), value: d.routing_number },
            ];
        }
        return [
            { label: t("wallet.full_name"), value: d.full_name },
            { label: t("wallet.phone_number"), value: `${d.phone_code || ""}${d.phone || ""}` },
        ];
    };

    return (
        <div data-testid="admin-payouts-page" className="space-y-4">
            <h1 className="font-heading font-black text-3xl">{t("admin.payouts")}</h1>
            <div className="space-y-3">
                {list.map((p) => (
                    <div key={p.id} data-testid={`admin-payout-${p.id}`} className="bento-card p-4 space-y-3">
                        <div className="flex flex-col md:flex-row gap-3 items-start md:items-center md:justify-between">
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-lg">{formatCurrency(p.amount, p.currency_code)}</div>
                                <div className="text-sm">{p.organizer?.organizer_name || `${p.organizer?.first_name || ""} ${p.organizer?.last_name || ""}`.trim()}</div>
                                <div className="text-xs text-muted-foreground">{p.organizer?.email}</div>
                            </div>
                            <div className="flex-shrink-0">
                                {p.status === "pending" ? (
                                    <button onClick={() => process(p.id)} data-testid={`process-${p.id}`} className="btn-primary !py-2 !px-4 text-sm inline-flex items-center gap-2">
                                        <CheckCircle className="h-4 w-4" /> {t("admin.mark_processed")}
                                    </button>
                                ) : (
                                    <span className="badge-pill bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]">{t("admin.processed")}</span>
                                )}
                            </div>
                        </div>
                        <div className="border-t border-border pt-3">
                            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                                {p.payout_method_details?.type === "bank_transfer" ? t("wallet.bank_account") : t("wallet.moncash_account")}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {buildPayoutFields(p.payout_method_details).map((f, idx) => (
                                    <div
                                        key={idx}
                                        className="flex items-center justify-between gap-2 p-2 rounded-lg bg-muted"
                                    >
                                        <div className="min-w-0 flex-1">
                                            <div className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider">
                                                {f.label}
                                            </div>
                                            <div className="text-sm font-mono truncate" title={f.value}>{f.value || "-"}</div>
                                        </div>
                                        <button
                                            data-testid={`copy-${p.id}-${idx}`}
                                            onClick={() => copyToClipboard(f.value)}
                                            disabled={!f.value}
                                            className="flex-shrink-0 p-1.5 rounded-md hover:bg-background transition-colors disabled:opacity-40"
                                            title={t("admin.copy")}
                                            aria-label={t("admin.copy")}
                                        >
                                            <Copy className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function AdminSystemPage() {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [savingId, setSavingId] = useState(null);

    const load = async () => {
        const { data } = await api.get("/api/admin/system-settings");
        setRows(data || []);
    };
    useEffect(() => { load(); }, []);

    const update = (country_id, patch) => {
        setRows((prev) => prev.map((r) => r.country_id === country_id ? { ...r, ...patch } : r));
    };

    const save = async (row) => {
        setSavingId(row.country_id);
        try {
            await api.put("/api/admin/system-settings", {
                country_id: row.country_id,
                fee_percentage: Number(row.fee_percentage),
                fee_fixed_amount: Number(row.fee_fixed_amount),
            });
            toast.success(t("admin.settings_saved"));
            load();
        } catch {
            toast.error(t("common.error"));
        } finally {
            setSavingId(null);
        }
    };

    return (
        <div data-testid="admin-system-page" className="space-y-6 max-w-3xl">
            <h1 className="font-heading font-black text-3xl">{t("admin.system")}</h1>
            <p className="text-sm text-muted-foreground">{t("admin.system_subtitle")}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {rows.map((r) => (
                    <div key={r.country_id} data-testid={`fee-card-${r.country?.code || r.country_id}`} className="bento-card p-5 space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="font-bold text-lg">
                                {r.country?.name || r.country_id}
                            </h3>
                            <span className="badge-pill bg-muted text-foreground">
                                {r.country?.currency_code || ""}
                            </span>
                        </div>
                        <div>
                            <label className="text-sm font-semibold block mb-1.5">{t("admin.fee_percentage")} (%)</label>
                            <input
                                data-testid={`fee-pct-${r.country?.code || r.country_id}`}
                                type="number"
                                step="0.01"
                                min="0"
                                value={r.fee_percentage ?? 0}
                                onChange={(e) => update(r.country_id, { fee_percentage: e.target.value })}
                                className="input-modern"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-semibold block mb-1.5">{t("admin.fee_fixed")} ({r.country?.currency_code || ""})</label>
                            <input
                                data-testid={`fee-fixed-${r.country?.code || r.country_id}`}
                                type="number"
                                step="0.01"
                                min="0"
                                value={r.fee_fixed_amount ?? 0}
                                onChange={(e) => update(r.country_id, { fee_fixed_amount: e.target.value })}
                                className="input-modern"
                            />
                        </div>
                        <button
                            data-testid={`save-system-${r.country?.code || r.country_id}`}
                            onClick={() => save(r)}
                            disabled={savingId === r.country_id}
                            className="btn-primary w-full disabled:opacity-50"
                        >
                            {savingId === r.country_id ? t("common.loading") : t("admin.save")}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function AdminTopEventsPage() {
    const { t } = useTranslation();
    const [list, setList] = useState([]);
    const [events, setEvents] = useState([]);
    const [selected, setSelected] = useState("");

    const load = async () => {
        const [a, b] = await Promise.all([api.get("/api/admin/top-events"), api.get("/api/admin/events")]);
        setList(a.data || []);
        setEvents((b.data || []).filter((e) => e.status === "live"));
    };
    useEffect(() => { load(); }, []);

    const add = async () => {
        if (!selected) return;
        try { await api.post("/api/admin/top-events", { event_id: selected }); setSelected(""); load(); } catch (e) { toast.error(e?.response?.data?.detail); }
    };
    const remove = async (id) => { await api.delete(`/api/admin/top-events/${id}`); load(); };
    const reorder = async (idx, dir) => {
        const newList = [...list];
        const swap = idx + dir;
        if (swap < 0 || swap >= newList.length) return;
        [newList[idx], newList[swap]] = [newList[swap], newList[idx]];
        setList(newList);
        await api.put("/api/admin/top-events/reorder", { ordered_ids: newList.map((x) => x.id) });
    };

    const notIn = events.filter((e) => !list.find((x) => x.event_id === e.id));

    return (
        <div data-testid="admin-top-events-page" className="space-y-6">
            <h1 className="font-heading font-black text-3xl">{t("admin.top_events")}</h1>
            <div className="bento-card p-5 flex flex-col sm:flex-row gap-2 items-end">
                <div className="flex-1 w-full">
                    <label className="text-sm font-semibold block mb-1.5">{t("admin.add_top_event")}</label>
                    <SearchableSelect
                        testId="top-event-select"
                        options={notIn.map((e) => ({ value: e.id, label: e.title }))}
                        value={selected}
                        onValueChange={setSelected}
                        placeholder={t("common.choose")}
                        searchPlaceholder={t("common.search")}
                    />
                </div>
                <button data-testid="top-event-add" onClick={add} disabled={!selected} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">{t("admin.add")}</button>
            </div>
            <div className="space-y-2">
                {list.map((row, idx) => (
                    <div key={row.id} data-testid={`top-row-${row.id}`} className="bento-card p-4 flex items-center gap-3">
                        <div className="font-bold text-xl w-8 text-center">#{idx + 1}</div>
                        <div className="flex-1 font-semibold">{row.event?.title}</div>
                        <button onClick={() => reorder(idx, -1)} className="p-2 hover:bg-muted rounded-lg">↑</button>
                        <button onClick={() => reorder(idx, 1)} className="p-2 hover:bg-muted rounded-lg">↓</button>
                        <button onClick={() => remove(row.id)} className="p-2 hover:bg-destructive/10 text-destructive rounded-lg">{t("admin.remove")}</button>
                    </div>
                ))}
            </div>
        </div>
    );
}
